import { Dayjs } from 'dayjs'
import { useCallback, useRef, useState, VFC } from 'react'
import TextareaAutosize from 'react-textarea-autosize'
import { ApiResponse } from 'src/api/types'
import { SmallButton } from 'src/elements/Button'
import { useErrorMessageModal } from 'src/hooks/useModal'
import {
  _messagebutton,
  _messagebuttonbg,
  _messageformborder,
  _messageiconfill,
} from 'src/styles/colors'
import { fontWeightMedium, fontWeightRegular } from 'src/styles/font'
import { flexCenter } from 'src/styles/mixins'
import styled from 'styled-components'
import { RequestBoxCompany } from '../../../models/RequestBox'
import { AttentionModal } from '../Modals/AttentionModal'
import { MessageTemplateModal } from '../Modals/MessageTemplateModal'
import { ScheduleMessageModal } from '../Modals/ScheduleMessageModal'
import { AttachementsPreview } from './AttachmentsPreview'
import { ScheduleUrlBanner } from './Message/ScheduleUrl'
import { MessageFormToolbar } from './MessageFormToolbar'

export { MessageForm }

export type MessageFormProps = {
  postMessage: (content: {
    text: string
    files?: File[]
    scheduledAt?: Dayjs
    noAttention?: boolean
  }) => Promise<ApiResponse>
  onlyPlainText?: boolean
  requestBox?: RequestBoxCompany
  sentRequest: boolean
  myScheduleUrl?: string
  showMessageAttention: boolean
  className?: string
}
const MessageFormComponent: VFC<MessageFormProps> = ({
  postMessage,
  showMessageAttention,
  requestBox,
  sentRequest,
  myScheduleUrl,
  className,
  onlyPlainText,
}) => {
  const textareaEl = useRef<HTMLTextAreaElement>(null)
  const [text, setText] = useState('')
  const [selectedFiles, setFiles] = useState<File[]>([])
  const [scheduledAt, setScheduledAt] = useState<Dayjs>()
  const [isMessageTemplateModalOpen, setMessageTemplateModalOpen] =
    useState(false)
  const [isScheduleMessageModalOpen, setScheduleMessageModalOpen] =
    useState(false)
  const [isAttentionModalOpen, setIsAttentionModalOpen] = useState(false)
  const [sending, setSending] = useState(false)
  const removeFile = useCallback(
    (index: number) => {
      setFiles(selectedFiles.filter((_, idx) => idx !== index))
    },
    [selectedFiles],
  )
  const { open } = useErrorMessageModal()
  const send = async (noAttention?: boolean) => {
    if (sending) return
    setSending(true)
    const res = await postMessage({
      text,
      files: selectedFiles,
      scheduledAt,
      noAttention,
    })
    if (res.error) {
      open({ message: res.error.errorMessages?.join('\n') })
      setSending(false)
      return
    }
    setText('')
    setFiles([])
    setScheduledAt(undefined)
    setSending(false)
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // cmd + Enter もしくは ctrl + Enter
    if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
      if (showMessageAttention) {
        setIsAttentionModalOpen(showMessageAttention)
        return
      }
      send()
    }
  }

  return (
    <>
      <div className={className}>
        {/* NOTE: どちらかがIS代行の場合は表示しないしない */}
        {requestBox && requestBox.schedule?.shouldDisplay && (
          <ScheduleUrlBanner
            requestBox={requestBox}
            myScheduleUrl={myScheduleUrl}
            sentRequest={sentRequest}
          />
        )}
        <MessageFormDiv>
          {selectedFiles.length > 0 && (
            <AttachementsPreview
              files={selectedFiles}
              removeFile={removeFile}
            />
          )}
          <TextareaAutosize
            ref={textareaEl}
            value={text}
            minRows={2}
            maxRows={8}
            onChange={({ target: { value } }) => setText(value)}
            onKeyDown={handleKeyDown}
            placeholder={
              'メッセージを入力\n' +
              '(win) Ctrl+Enterで送信\n' +
              '(Mac) Cmnd+Enterで送信'
            }
          />
          <Control>
            <div>
              {!onlyPlainText && (
                <MessageFormToolbar
                  setMessageTemplateModalOpen={setMessageTemplateModalOpen}
                  setFiles={setFiles}
                  selectedFiles={selectedFiles}
                  setScheduleMessageModalOpen={setScheduleMessageModalOpen}
                  scheduledAt={scheduledAt}
                />
              )}
            </div>
            <SubmitButton
              onClick={async () => {
                if (showMessageAttention) {
                  setIsAttentionModalOpen(showMessageAttention)
                  return
                }
                send()
              }}
            >
              送信
            </SubmitButton>
          </Control>
        </MessageFormDiv>
      </div>
      {isMessageTemplateModalOpen && (
        <MessageTemplateModal
          insert={(template: string) => {
            const { selectionStart = 0, selectionEnd = 0 } =
              textareaEl.current || {}
            const inserted = `${text.slice(
              0,
              selectionStart,
            )}${template}${text.slice(selectionEnd)}`
            setText(inserted)
          }}
          close={() => setMessageTemplateModalOpen(false)}
        />
      )}
      {isScheduleMessageModalOpen && (
        <ScheduleMessageModal
          close={() => setScheduleMessageModalOpen(false)}
          scheduledAt={scheduledAt}
          setScheduledAt={setScheduledAt}
        />
      )}
      {isAttentionModalOpen && (
        <AttentionModal
          close={() => setIsAttentionModalOpen(false)}
          send={send}
        />
      )}
    </>
  )
}
const MessageForm = styled(MessageFormComponent)``

const MessageFormDiv = styled.div`
  border: 1px solid ${_messageformborder};
  border-radius: 5px;

  background-color: #ffffffc0;
  backdrop-filter: blur(50px) brightness(1.75);
  padding: 14px 8px 8px 24px;

  textarea {
    width: 100%;
    resize: none;
    font-size: 14px;
    font-weight: ${fontWeightMedium};
    letter-spacing: 0.036em;
    line-height: 1.5;
    overflow-y: auto;
    cursor: auto;

    ::placeholder {
      font-size: 12px;
      font-weight: ${fontWeightRegular};
    }
  }
`

const Control = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 16px 0 0;

  > div {
    display: flex;
    align-items: center;
    > * {
      margin: 0 16px;
    }
    svg {
      color: ${_messageiconfill};
      height: 14px;
    }
    button,
    label {
      cursor: pointer;
      ${flexCenter};
    }
    p {
      margin-left: 4px;
      font-size: 12px;
    }
  }
`
const SubmitButton = styled(SmallButton)`
  width: 83px;
  color: ${_messagebutton};
  background: ${_messagebuttonbg};
`
